
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TerraceSubmission } from "@/types/terrace";
import { TerraceFormValues, terraceFormSchema } from "./SubmissionFormSchema";

// Type guard for edge-case legacy/dirty objects masquerading as original_maps_url
function hasStringValueProp(obj: unknown): obj is { value: string } {
  return (
    !!obj &&
    typeof obj === "object" &&
    "value" in obj &&
    typeof (obj as any).value === "string"
  );
}

export const useSubmissionViewing = () => {
  const [selectedSubmission, setSelectedSubmission] = useState<TerraceSubmission | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm<TerraceFormValues>({
    resolver: zodResolver(terraceFormSchema),
    defaultValues: {
      name: "",
      type: "",
      address: "",
      lat: undefined,
      lng: undefined,
      openingHours: "",
      detailedOpeningHours: {
        monday: { isOpen: false, from: "", to: "" },
        tuesday: { isOpen: false, from: "", to: "" },
        wednesday: { isOpen: false, from: "", to: "" },
        thursday: { isOpen: false, from: "", to: "" },
        friday: { isOpen: false, from: "", to: "" },
        saturday: { isOpen: false, from: "", to: "" },
        sunday: { isOpen: false, from: "", to: "" },
      },
      orientation: "unknown",
      hasFood: false,
      petFriendly: false,
      servesAlcohol: false,
      terraceDescription: "",
      website: "",
      phone: "",
      sunStatus: "not-sunny",
      submitterName: "",
      googleMapsUrl: "",
    },
  });

  const handleViewSubmission = (submission: TerraceSubmission) => {
    setSelectedSubmission(submission);

    // Parse detailed opening hours if available
    let detailedOpeningHours = {
      monday: { isOpen: false, from: "", to: "" },
      tuesday: { isOpen: false, from: "", to: "" },
      wednesday: { isOpen: false, from: "", to: "" },
      thursday: { isOpen: false, from: "", to: "" },
      friday: { isOpen: false, from: "", to: "" },
      saturday: { isOpen: false, from: "", to: "" },
      sunday: { isOpen: false, from: "", to: "" },
    };

    if (submission.detailed_opening_hours) {
      try {
        detailedOpeningHours = typeof submission.detailed_opening_hours === 'string'
          ? JSON.parse(submission.detailed_opening_hours)
          : submission.detailed_opening_hours;
      } catch (e) {
        console.warn("Failed to parse detailed opening hours:", e);
      }
    }

    // Validate orientation value - ensure it matches the enum or default to "unknown"
    const validOrientations = ["N", "NE", "E", "SE", "S", "SW", "W", "NW", "unknown"] as const;
    const orientation = submission.orientation && validOrientations.includes(submission.orientation as any)
      ? submission.orientation as typeof validOrientations[number]
      : "unknown";

    // --- Robust handling for original_maps_url (string or object) ---
    let googleMapsUrl = "";
    if (typeof submission.original_maps_url === "string") {
      googleMapsUrl = submission.original_maps_url;
    } else if (hasStringValueProp(submission.original_maps_url)) {
      // Use a strongly typed local variable for TypeScript
      const valObj = submission.original_maps_url as { value: string };
      googleMapsUrl = valObj.value;
    } else {
      googleMapsUrl = "";
    }

    const formData: TerraceFormValues = {
      name: submission.name,
      type: submission.type,
      address: submission.address,
      lat: submission.lat,
      lng: submission.lng,
      openingHours: submission.openingHours || "",
      detailedOpeningHours: detailedOpeningHours,
      orientation: orientation,
      hasFood: submission.hasFood,
      petFriendly: submission.petFriendly,
      servesAlcohol: submission.servesAlcohol,
      terraceDescription: submission.terraceDescription || "",
      website: submission.website || "",
      phone: submission.phone || "",
      sunStatus: submission.sunStatus,
      submitterName: submission.submitter_name || "",
      photo_url: submission.photo_url || "",
      googleMapsUrl, // --- Always a clean string (even if "") ---
    };

    form.reset(formData);
    setIsDialogOpen(true);
  };

  return {
    selectedSubmission,
    isDialogOpen,
    setIsDialogOpen,
    form,
    handleViewSubmission,
  };
};
